// Starts from the browser's saved copy (pinia/theme.ts) so the first paint is
// in the right accent.
import { queryOptions, useQuery } from "@tanstack/vue-query";
import { getActivePinia } from "pinia";
import { computed, reactive, watch } from "vue";

import { getTheme } from "../api/portal";
import { useThemeStore, type AccentTheme } from "../pinia/theme";
import { applyAccent } from "../utils/portalAccent";

export const themeKeys = {
  portal: ["theme"] as const,
};

// The accent changes only when an admin saves it, which invalidates this.
const THEME_FRESH_MS = 10 * 60 * 1000;

// Without Pinia, no saved copy is read or written.
export function themeQueryOptions() {
  const lastSeen = getActivePinia() ? useThemeStore() : null;
  return queryOptions({
    queryKey: themeKeys.portal,
    queryFn: async ({ signal }) => {
      const theme = await getTheme({ signal });
      if (lastSeen) {
        lastSeen.theme = theme;
        lastSeen.themeFetchedAt = Date.now();
      }
      return theme;
    },
    staleTime: THEME_FRESH_MS,
    initialData: () => lastSeen?.theme ?? undefined,
    initialDataUpdatedAt: () => lastSeen?.themeFetchedAt ?? 0,
  });
}

export function useTheme(): { readonly accent: AccentTheme; readonly highAccessibility: boolean } {
  const query = useQuery(themeQueryOptions());
  return reactive({
    accent: computed(() => (query.data.value?.Accent as AccentTheme | undefined) ?? "grape"),
    highAccessibility: computed(() => query.data.value?.HighAccessibility ?? false),
  });
}

// Puts each answer the server gives on <html data-accent> (utils/portalAccent.ts).
// The saved copy the query starts from is not an answer, and neither is a
// failure, so neither writes: the shell's mark or the saved copy stays.
export function useAccentAttribute(): void {
  const query = useQuery(themeQueryOptions());
  watch(query.dataUpdatedAt, () => {
    if (query.data.value) applyAccent(query.data.value);
  });
}
